'use client';
import Head from 'next/head';
import { useState, useMemo } from 'react';
import { Shapes } from 'lucide-react';
import MethodPageLayout from '@/components/layouts/MethodPageLayout';
import ShapeVisualiser from '@/components/methods/ShapeVisualiser';
import StepperControl from '@/components/common/StepperControl';
import SliderControl from '@/components/common/SliderControl';
import ColorSwatchRow from '@/components/common/ColorSwatchRow';

interface ShapeTypeOption {
    value: string;
    label: string;
}

const SHAPE_TYPES: ShapeTypeOption[] = [
    { value: 'circle', label: 'Circle' },
    { value: 'square', label: 'Square' },
    { value: 'rectangle', label: 'Rectangle' },
    { value: 'ellipse', label: 'Ellipse' },
    { value: 'regular-polygon', label: 'Regular Polygon' },
    { value: 'triangle-right', label: 'Right-Angled Triangle' },
    { value: 'triangle-equilateral', label: 'Equilateral Triangle' },
    { value: 'triangle-isosceles', label: 'Isosceles Triangle' },
    { value: 'triangle-scalene', label: 'Scalene Triangle' },
    { value: 'trapezium', label: 'Trapezium' },
    { value: 'parallelogram', label: 'Parallelogram' },
    { value: 'rhombus', label: 'Rhombus' },
    { value: 'kite', label: 'Kite' },
    { value: 'star', label: 'Star' },
];

const SHAPE_COLORS = ['#534AB7', '#0F6E56', '#854F0B', '#185FA5', '#993C1D', '#f4a261'];

export default function ShapeVisualiserPage() {
    const [type, setType] = useState('circle');
    const [sides, setSides] = useState(5);
    const [radius, setRadius] = useState(120);
    const [width, setWidth] = useState(250);
    const [height, setHeight] = useState(150);
    const [fillColor, setFillColor] = useState('#854F0B');
    const [showAngles, setShowAngles] = useState(false);
    const [angleOverride, setAngleOverride] = useState('');

    const onReset = () => {
        setType('circle');
        setSides(5);
        setRadius(120);
        setWidth(250);
        setHeight(150);
        setFillColor('#854F0B');
        setShowAngles(false);
        setAngleOverride('');
    };

    const options = useMemo(
        () => ({
            sides: sides || 3,
            radius: radius || 100,
            width: width || 200,
            height: height || 100,
            fillColor,
            showAngles,
            angleOverride,
        }),
        [sides, radius, width, height, fillColor, showAngles, angleOverride],
    );

    const labelClass = 'text-[11px] font-medium uppercase text-muted';

    const controls = (
        <>
            <div className="flex flex-col gap-2">
                <label className={labelClass}>Shape Type</label>
                <select value={type} onChange={(e) => setType(e.target.value)}>
                    {SHAPE_TYPES.map((t) => (
                        <option key={t.value} value={t.value}>
                            {t.label}
                        </option>
                    ))}
                </select>
            </div>

            {(type === 'circle' || type === 'regular-polygon' || type === 'star') && (
                <SliderControl
                    label="Radius (px)"
                    value={radius}
                    min={10}
                    max={200}
                    onChange={setRadius}
                />
            )}

            {(type === 'regular-polygon' || type === 'star') && (
                <StepperControl
                    label={type === 'star' ? 'Points' : 'Sides'}
                    value={sides}
                    min={3}
                    max={20}
                    onChange={setSides}
                />
            )}

            {(type === 'rectangle' ||
                type === 'square' ||
                type === 'ellipse' ||
                type.startsWith('triangle-') ||
                type === 'trapezium' ||
                type === 'parallelogram' ||
                type === 'rhombus' ||
                type === 'kite') && (
                <>
                    <SliderControl
                        label={
                            type === 'triangle-equilateral' || type === 'square'
                                ? 'Side Length (px)'
                                : 'Width (px)'
                        }
                        value={width}
                        min={10}
                        max={350}
                        onChange={setWidth}
                    />
                    {type !== 'triangle-equilateral' && type !== 'square' && (
                        <SliderControl
                            label="Height (px)"
                            value={height}
                            min={10}
                            max={350}
                            onChange={setHeight}
                        />
                    )}
                </>
            )}

            <ColorSwatchRow
                label="Fill Color"
                colors={SHAPE_COLORS}
                value={fillColor}
                onChange={setFillColor}
            />

            <div className="mt-1 flex flex-col gap-2.5">
                <label className="flex cursor-pointer items-center gap-2 text-[13px]">
                    <input
                        type="checkbox"
                        checked={showAngles}
                        onChange={(e) => setShowAngles(e.target.checked)}
                        className="w-auto"
                    />
                    Show Angles
                </label>
            </div>

            {showAngles && (
                <div className="flex flex-col gap-2">
                    <label className={labelClass}>Angle Label Override</label>
                    <input
                        type="text"
                        value={angleOverride}
                        onChange={(e) => setAngleOverride(e.target.value)}
                        placeholder="e.g. A, B, C"
                    />
                    <p className="-mt-1 text-[11px] text-hint">
                        Use commas to override multiple angles.
                    </p>
                </div>
            )}
        </>
    );

    return (
        <>
            <Head>
                <title>Shape Visualiser</title>
            </Head>
            <MethodPageLayout
                icon={<Shapes size={20} />}
                iconColor="amber"
                title="Shape visualiser"
                description="Generate and customise various mathematical shapes."
                category="visualiser"
                controls={controls}
                onReset={onReset}
            >
                <ShapeVisualiser type={type} options={options} />
            </MethodPageLayout>
        </>
    );
}
